// manufacturing/RawMaterialMaster.jsx - COMPLETE FIXED VERSION

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Edit, Trash2, Printer, ChevronUp,
    Database, Package, DollarSign, AlertCircle, CheckCircle, XCircle,
    Users,
} from 'feather-icons-react/build/IconComponents';
import { rawMaterialAPI, categoryAPI, unitAPI, supplierAPI } from "../../core/action/api";
import ImageWithBasePath from '../../core/img/imagewithbasebath';

const MySwal = withReactContent(Swal);

const RawMaterialMaster = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [rawMaterials, setRawMaterials] = useState([]);
    const [categories, setCategories] = useState([]);
    const [units, setUnits] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        material_code: '',
        material_name: '',
        sku: '',
        category_id: '',
        unit_id: '',
        unit_name: 'PCS',
        cost_price: 0,
        selling_price: 0,
        min_stock_level: 10,
        max_stock_level: 1000,
        reorder_level: 100,
        description: '',
        supplier_id: '',
        hsn_code: '',
        tax_rate_id: '',
        status: 'active'
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        category_id: '',
        status: '',
        supplier_id: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'discontinued', label: 'Discontinued' }
    ];

    const canCreate = true;
    const canEdit = true;
    const canDelete = true;

    // Load data on mount
    useEffect(() => {
        loadRawMaterials();
        loadCategories();
        loadUnits();
        loadSuppliers();
    }, []);

    // Load raw materials - FIXED
    const loadRawMaterials = async (page = pagination.page) => {
        debugger;
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                category_id: filters.category_id,
                status: filters.status,
                supplier_id: filters.supplier_id
            };
            const response = await rawMaterialAPI.getAll(params);
            console.log('Load Raw Materials Response:', response);

            let data = [];
            let total = 0;
            let currentPage = page;
            let totalPages = 1;

            if (response?.success === true) {
                data = response.data || [];
                total = response.total || data.length;
                currentPage = response.page || page;
                totalPages = response.total_pages || Math.ceil(total / pagination.limit) || 1;
            } else if (response?.data && Array.isArray(response.data)) {
                data = response.data;
                total = response.total || data.length;
            } else if (Array.isArray(response)) {
                data = response;
                total = response.length;
            }

            const sortedData = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setRawMaterials(sortedData);
            setPagination({
                page: currentPage,
                limit: pagination.limit,
                total: total,
                totalPages: totalPages
            });

            if (data.length === 0 && filters.search) {
                toast.info('No raw materials found matching your search');
            }
        } catch (error) {
            debugger;
            console.error('Error loading raw materials:', error);
            toast.error('Failed to load raw materials');
            setRawMaterials([]);
        } finally {
            setLoading(false);
        }
    };

    // Extract categories from response
    const extractCategories = (response) => {
        if (Array.isArray(response?.data?.categories)) return response.data.categories;
        if (Array.isArray(response?.data?.parent_categories)) return response.data.parent_categories;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
    };

    // Extract units from response
    const extractUnits = (response) => {
        if (Array.isArray(response?.data?.units)) return response.data.units;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
    };

    // Load categories for dropdown
    const loadCategories = async () => {
        try {
            const response = await categoryAPI.list({ limit: 1000 });
            const categoryList = extractCategories(response);
            setCategories(categoryList);
        } catch (error) {
            console.error('Error loading categories:', error);
            toast.error('Failed to load categories');
        }
    };

    // Load units for dropdown
    const loadUnits = async () => {
        try {
            const response = await unitAPI.list({ limit: 1000 });
            const unitList = extractUnits(response);
            setUnits(unitList);
        } catch (error) {
            console.error('Error loading units:', error);
            toast.error('Failed to load units');
        }
    };

    // Load suppliers for dropdown
    const loadSuppliers = async () => {
        try {
            const response = await supplierAPI.getAll({ limit: 1000 });
            if (response?.success && response?.data) {
                setSuppliers(response.data);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setTimeout(() => loadRawMaterials(1), 100);
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Auto-generate material code if not manually set
        if (name === 'material_name' && !formData.material_code) {
            const code = value.toUpperCase().replace(/[^A-Z0-9]/g, '_').substring(0, 20);
            setFormData(prev => ({ ...prev, material_code: `RM-${code}` }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle select change
    const handleSelectChange = (selected, name) => {
        setFormData(prev => ({ ...prev, [name]: selected?.value || '' }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Generate SKU automatically
    const generateSKU = () => {
        const prefix = 'RM';
        const timestamp = Date.now().toString().slice(-6);
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const sku = `${prefix}-${timestamp}-${random}`;
        setFormData(prev => ({ ...prev, sku }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.material_code) {
            newErrors.material_code = 'Material code is required';
        }
        if (!formData.material_name) {
            newErrors.material_name = 'Material name is required';
        }
        if (!formData.sku) {
            newErrors.sku = 'SKU is required';
        }
        if (!formData.category_id) {
            newErrors.category_id = 'Category is required';
        }
        if (!formData.unit_id) {
            newErrors.unit_id = 'Unit is required';
        }
        if (formData.cost_price < 0) {
            newErrors.cost_price = 'Cost price cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle create submission - FIXED with proper error handling
    const handleCreateSubmit = async () => {
        console.log('Submitting form data:', formData);

        // Validate form
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setSubmitting(true);

        try {
            const result = await rawMaterialAPI.create(formData);
            console.log('API Response:', result);

            // Check if creation was successful
            if (result && result.success === true) {
                // Show success message
                toast.success(result.message || 'Raw material created successfully');

                // Reset form and close modal
                resetForm();
                setShowCreateModal(false);

                // Reload the list to show new item
                await loadRawMaterials(1);

                // Show success alert
                MySwal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: result.message || 'Raw material created successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                // Show error message from API
                const errorMsg = result?.message || result?.error || 'Failed to create raw material';
                toast.error(errorMsg);

                // Display error in modal
                setErrors({ form: errorMsg });

                // Show alert for duplicate
                if (errorMsg.includes('already exists')) {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Duplicate Entry',
                        text: errorMsg,
                        confirmButtonColor: '#d33'
                    });
                }
            }
        } catch (error) {
            console.error('Error creating raw material:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to create raw material';
            toast.error(errorMsg);
            setErrors({ form: errorMsg });
        } finally {
            setSubmitting(false);
        }
    };

    // Full working handleEditSubmit function
    const handleEditSubmit = async () => {
        console.log('========== EDIT SUBMIT START ==========');
        console.log('Selected Material:', selectedMaterial);
        console.log('Form Data:', formData);

        // Validate form
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        if (!selectedMaterial || !selectedMaterial.id) {
            toast.error('No material selected for update');
            return;
        }

        setSubmitting(true);

        try {
            // Create update payload
            const updatePayload = {
                id: selectedMaterial.id,
                material_code: formData.material_code,
                material_name: formData.material_name,
                sku: formData.sku,
                category_id: formData.category_id,
                unit_id: formData.unit_id,
                unit_name: formData.unit_name,
                cost_price: parseFloat(formData.cost_price) || 0,
                selling_price: parseFloat(formData.selling_price) || 0,
                min_stock_level: parseInt(formData.min_stock_level) || 10,
                max_stock_level: parseInt(formData.max_stock_level) || 1000,
                reorder_level: parseInt(formData.reorder_level) || 100,
                description: formData.description || '',
                supplier_id: formData.supplier_id || null,
                hsn_code: formData.hsn_code || '',
                tax_rate_id: formData.tax_rate_id || null,
                status: formData.status
            };

            console.log('Update Payload:', updatePayload);

            // Call the API
            const result = await rawMaterialAPI.update(selectedMaterial.id, updatePayload);
            console.log('API Response:', result);

            // Check response
            if (result && result.success === true) {
                // Success
                toast.success(result.message || 'Raw material updated successfully');

                // Close modal
                setShowEditModal(false);

                // Reset form
                resetForm();

                // Reload the list
                await loadRawMaterials(pagination.page);

                // Show success popup
                MySwal.fire({
                    icon: 'success',
                    title: 'Updated!',
                    text: result.message || 'Raw material updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                // Error from API
                const errorMsg = result?.message || result?.error || 'Failed to update raw material';
                console.error('API returned error:', errorMsg);
                toast.error(errorMsg);
                setErrors({ form: errorMsg });
            }
        } catch (error) {
            console.error('Exception in handleEditSubmit:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to update raw material';
            toast.error(errorMsg);
            setErrors({ form: errorMsg });
        } finally {
            setSubmitting(false);
            console.log('========== EDIT SUBMIT END ==========');
        }
    };

    // Handle delete
    const handleDelete = async (material) => {
        const result = await MySwal.fire({
            title: 'Delete Raw Material?',
            html: `<p>Are you sure you want to delete <strong>${material.material_name}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await rawMaterialAPI.delete(material.id);
                if (response && response.success === true) {
                    toast.success(response.message || 'Raw material deleted successfully');
                    await loadRawMaterials(pagination.page);
                } else {
                    toast.error(response?.message || 'Failed to delete raw material');
                }
            } catch (error) {
                console.error('Error deleting raw material:', error);
                toast.error('Failed to delete raw material');
            }
        }
    };

    // Edit material
    const handleEdit = (material) => {
        setSelectedMaterial(material);
        setFormData({
            material_code: material.material_code || '',
            material_name: material.material_name || '',
            sku: material.sku || '',
            category_id: material.category_id?.toString() || '',
            unit_id: material.unit_id?.toString() || '',
            unit_name: material.unit_name || 'PCS',
            cost_price: material.cost_price || 0,
            selling_price: material.selling_price || 0,
            min_stock_level: material.min_stock_level || 10,
            max_stock_level: material.max_stock_level || 1000,
            reorder_level: material.reorder_level || 100,
            description: material.description || '',
            supplier_id: material.supplier_id?.toString() || '',
            hsn_code: material.hsn_code || '',
            tax_rate_id: material.tax_rate_id?.toString() || '',
            status: material.status || 'active'
        });
        setShowEditModal(true);
    };

    // View material
    const handleView = (material) => {
        setSelectedMaterial(material);
        setShowViewModal(true);
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            material_code: '',
            material_name: '',
            sku: '',
            category_id: '',
            unit_id: '',
            unit_name: 'PCS',
            cost_price: 0,
            selling_price: 0,
            min_stock_level: 10,
            max_stock_level: 1000,
            reorder_level: 100,
            description: '',
            supplier_id: '',
            hsn_code: '',
            tax_rate_id: '',
            status: 'active'
        });
        setErrors({});
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const map = {
            active: { class: 'bg-success', icon: <CheckCircle size={12} className="me-1" />, label: 'Active' },
            inactive: { class: 'bg-secondary', icon: <XCircle size={12} className="me-1" />, label: 'Inactive' },
            discontinued: { class: 'bg-danger', icon: <AlertCircle size={12} className="me-1" />, label: 'Discontinued' }
        };
        const config = map[status] || map.active;
        return (
            <span className={`badge ${config.class} d-inline-flex align-items-center`}>
                {config.icon}
                {config.label}
            </span>
        );
    };

    // Category options
    const categoryOptions = categories.map((cat) => ({
        value: cat.id?.toString(),
        label: cat.category_name || cat.name || 'Unnamed Category'
    })).filter((cat) => cat.value);

    // Unit options
    const unitOptions = units.map((unit) => ({
        value: unit.id?.toString(),
        label: `${unit.unit_name || unit.name || unit.Unit || 'Unit'} (${unit.short_name || unit.unit_symbol || 'PCS'})`
    })).filter((unit) => unit.value);

    // Supplier options
    const supplierOptions = suppliers.map(sup => ({
        value: sup.id.toString(),
        label: sup.name
    }));

    const selectedCategoryOption = categoryOptions.find((opt) => opt.value === formData.category_id) || null;
    const selectedUnitOption = unitOptions.find((opt) => opt.value === formData.unit_id) || null;
    const selectedSupplierOption = supplierOptions.find((opt) => opt.value === formData.supplier_id) || null;

    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Raw Material Product Entry</h4>
                        <h6>Manage raw materials used in manufacturing</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'PDF')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="PDF" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Excel')}>
                                <Link to="#">
                                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="Excel" />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <Link onClick={() => window.print()}>
                                    <Printer />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => loadRawMaterials()}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />
                                Add Raw Material
                            </button>
                        </div>
                    )}
                </div>

                {/* Statistics Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                        <Database size={24} className="text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Raw Materials</p>
                                        <h3 className="mb-0">{pagination.total}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                        <Package size={24} className="text-success" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Active Materials</p>
                                        <h3 className="mb-0">
                                            {rawMaterials.filter(m => m.status === 'active').length}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                        <DollarSign size={24} className="text-warning" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Avg Cost Price</p>
                                        <h3 className="mb-0">
                                            {formatCurrency(rawMaterials.reduce((sum, m) => sum + (m.cost_price || 0), 0) / (rawMaterials.length || 1))}
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="d-flex align-items-center">
                                    <div className="bg-info bg-opacity-10 p-3 rounded me-3">
                                        <Users size={24} className="text-info" />
                                    </div>
                                    <div>
                                        <p className="text-muted mb-1">Total Suppliers</p>
                                        <h3 className="mb-0">{new Set(rawMaterials.map(m => m.supplier_id)).size}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Material name, SKU, Code..."
                                            value={filters.search}
                                            onChange={(e) => handleFilterChange('search', e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && loadRawMaterials(1)}
                                        />
                                        <button className="btn btn-primary" onClick={() => loadRawMaterials(1)}>
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>Category</label>
                                    <Select
                                        options={categoryOptions}
                                        isClearable
                                        placeholder="All Categories"
                                        onChange={(selected) => handleFilterChange('category_id', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>Status</label>
                                    <Select
                                        options={statusOptions}
                                        isClearable
                                        placeholder="All Status"
                                        onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Supplier</label>
                                    <Select
                                        options={supplierOptions}
                                        isClearable
                                        placeholder="All Suppliers"
                                        onChange={(selected) => handleFilterChange('supplier_id', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100" onClick={() => {
                                        setFilters({ search: '', category_id: '', status: '', supplier_id: '' });
                                        loadRawMaterials(1);
                                    }}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Raw Materials Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : rawMaterials.length === 0 ? (
                            <div className="text-center py-5">
                                <Database size={48} className="text-muted mb-3" />
                                <h5>No raw materials found</h5>
                                <p className="text-muted">Add raw materials to start manufacturing</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />
                                        Add Raw Material
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedRows.length === rawMaterials.length}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedRows(rawMaterials.map(m => m.id));
                                                        } else {
                                                            setSelectedRows([]);
                                                        }
                                                    }}
                                                />
                                            </th>
                                            <th>Code</th>
                                            <th>Material Name</th>
                                            <th>SKU</th>
                                            <th>HSN Code</th>
                                            <th>Category</th>
                                            <th>Unit</th>
                                            <th>Cost Price</th>
                                            <th>Selling Price</th>
                                            <th>Current Stock</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rawMaterials.map((material) => (
                                            <tr key={material.id}>
                                                <td className="ps-3">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.includes(material.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows([...selectedRows, material.id]);
                                                            } else {
                                                                setSelectedRows(selectedRows.filter(id => id !== material.id));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{material.material_code}</strong>
                                                </td>
                                                <td>{material.material_name}</td>
                                                <td>
                                                    <code>{material.sku}</code>
                                                </td>
                                                <td>{material.hsn_code || <span className="text-muted">—</span>}</td>
                                                <td>{material.category_name}</td>
                                                <td>{material.unit_name || material.unit}</td>
                                                <td className="text-success">{formatCurrency(material.cost_price)}</td>
                                                <td>{formatCurrency(material.selling_price)}</td>
                                                <td className={material.quantity <= material.reorder_level ? 'text-danger fw-bold' : ''}>
                                                    {material.quantity || 0} {material.unit_name || 'PCS'}
                                                    {material.quantity <= material.reorder_level && (
                                                        <span className="badge bg-warning ms-1">Low Stock</span>
                                                    )}
                                                </td>
                                                <td>{getStatusBadge(material.status)}</td>
                                                <td className="text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-info me-1"
                                                        onClick={() => handleView(material)}
                                                        title="View Details"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    {/* {canEdit && material.status !== 'discontinued' && ( */}
                                                    {canEdit && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEdit(material)}
                                                            title="Edit"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                    )}
                                                    {/* {canDelete && material.status !== 'discontinued' && ( */}
                                                    {canDelete && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleDelete(material)}
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadRawMaterials(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = pagination.page;
                                            if (pagination.totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                pageNum = pagination.totalPages - 4 + i;
                                            } else {
                                                pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadRawMaterials(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadRawMaterials(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Raw Material Modal */}
            {showCreateModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Database size={18} className="me-2" />
                                    Add Raw Material
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {/* Display form error */}
                                {errors.form && (
                                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                                        <AlertCircle size={16} className="me-2" />
                                        {errors.form}
                                        <button type="button" className="btn-close" onClick={() => setErrors({})}></button>
                                    </div>
                                )}

                                <div className="row">
                                    {/* Basic Information */}
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Basic Information</h6>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Material Code *</label>
                                                        <input
                                                            type="text"
                                                            name="material_code"
                                                            className={`form-control ${errors.material_code ? 'is-invalid' : ''}`}
                                                            value={formData.material_code}
                                                            onChange={handleInputChange}
                                                            placeholder="RM-XXX"
                                                        />
                                                        {errors.material_code && <div className="text-danger small mt-1">{errors.material_code}</div>}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">SKU *</label>
                                                        <div className="input-group">
                                                            <input
                                                                type="text"
                                                                name="sku"
                                                                className={`form-control ${errors.sku ? 'is-invalid' : ''}`}
                                                                value={formData.sku}
                                                                onChange={handleInputChange}
                                                                placeholder="Auto-generated"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary"
                                                                onClick={generateSKU}
                                                                title="Generate SKU"
                                                            >
                                                                <RotateCcw size={14} />
                                                            </button>
                                                        </div>
                                                        {errors.sku && <div className="text-danger small mt-1">{errors.sku}</div>}
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Material Name *</label>
                                                        <input
                                                            type="text"
                                                            name="material_name"
                                                            className={`form-control ${errors.material_name ? 'is-invalid' : ''}`}
                                                            value={formData.material_name}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., Sticker Paper Roll, Adhesive Material"
                                                        />
                                                        {errors.material_name && <div className="text-danger small mt-1">{errors.material_name}</div>}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Category *</label>
                                                        <Select
                                                            options={categoryOptions}
                                                            value={selectedCategoryOption}
                                                            onChange={(selected) => handleSelectChange(selected, 'category_id')}
                                                            placeholder="Select category"
                                                            className={errors.category_id ? 'is-invalid' : ''}
                                                        />
                                                        {errors.category_id && <div className="text-danger small mt-1">{errors.category_id}</div>}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Unit *</label>
                                                        <Select
                                                            options={unitOptions}
                                                            value={selectedUnitOption}
                                                            onChange={(selected) => handleSelectChange(selected, 'unit_id')}
                                                            placeholder="Select unit"
                                                            className={errors.unit_id ? 'is-invalid' : ''}
                                                        />
                                                        {errors.unit_id && <div className="text-danger small mt-1">{errors.unit_id}</div>}
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Description</label>
                                                        <textarea
                                                            name="description"
                                                            className="form-control"
                                                            value={formData.description}
                                                            onChange={handleInputChange}
                                                            rows="2"
                                                            placeholder="Material description, specifications, etc."
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Pricing & Stock Settings */}
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Pricing & Stock Settings</h6>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Cost Price (₹) *</label>
                                                        <input
                                                            type="number"
                                                            name="cost_price"
                                                            className={`form-control ${errors.cost_price ? 'is-invalid' : ''}`}
                                                            value={formData.cost_price}
                                                            onChange={handleInputChange}

                                                            min="0"
                                                        />
                                                        {errors.cost_price && <div className="text-danger small mt-1">{errors.cost_price}</div>}
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Selling Price (₹)</label>
                                                        <input
                                                            type="number"
                                                            name="selling_price"
                                                            className={`form-control ${errors.selling_price ? 'is-invalid' : ''}`}
                                                            value={formData.selling_price}
                                                            onChange={handleInputChange}

                                                            min="0"
                                                        />
                                                        {errors.selling_price && <div className="text-danger small mt-1">{errors.selling_price}</div>}
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Min Stock Level</label>
                                                        <input
                                                            type="number"
                                                            name="min_stock_level"
                                                            className="form-control"
                                                            value={formData.min_stock_level}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                            step="any"
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Max Stock Level</label>
                                                        <input
                                                            type="number"
                                                            name="max_stock_level"
                                                            className="form-control"
                                                            value={formData.max_stock_level}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Reorder Level</label>
                                                        <input
                                                            type="number"
                                                            name="reorder_level"
                                                            className="form-control"
                                                            value={formData.reorder_level}
                                                            onChange={handleInputChange}
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Additional Information</h6>
                                                <div className="row">
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Supplier (Preferred)</label>
                                                        <Select
                                                            options={supplierOptions}
                                                            value={selectedSupplierOption}
                                                            onChange={(selected) => handleSelectChange(selected, 'supplier_id')}
                                                            placeholder="Select preferred supplier"
                                                            isClearable
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">HSN Code</label>
                                                        <input
                                                            type="text"
                                                            name="hsn_code"
                                                            className="form-control"
                                                            value={formData.hsn_code}
                                                            onChange={handleInputChange}
                                                            placeholder="e.g., 48219090"
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Status</label>
                                                        <Select
                                                            options={statusOptions}
                                                            value={statusOptions.find(opt => opt.value === formData.status)}
                                                            onChange={(selected) => handleSelectChange(selected, 'status')}
                                                            placeholder="Select status"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleCreateSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} className="me-2" />
                                            Save Raw Material
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Raw Material Modal - Keep same as before */}
            {showEditModal && selectedMaterial && (
                // ... (same as your existing edit modal)
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Edit size={18} className="me-2" />
                                    Edit Raw Material - {selectedMaterial.material_name}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {/* Your existing edit form */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Basic Information</h6>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Material Code *</label>
                                                        <input
                                                            type="text"
                                                            name="material_code"
                                                            className="form-control"
                                                            value={formData.material_code}
                                                            onChange={handleInputChange}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">SKU *</label>
                                                        <input
                                                            type="text"
                                                            name="sku"
                                                            className="form-control"
                                                            value={formData.sku}
                                                            onChange={handleInputChange}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Material Name *</label>
                                                        <input
                                                            type="text"
                                                            name="material_name"
                                                            className="form-control"
                                                            value={formData.material_name}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Category</label>
                                                        <Select
                                                            options={categoryOptions}
                                                            value={selectedCategoryOption}
                                                            onChange={(selected) => handleSelectChange(selected, 'category_id')}
                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Unit</label>
                                                        <Select
                                                            options={unitOptions}
                                                            value={selectedUnitOption}
                                                            onChange={(selected) => handleSelectChange(selected, 'unit_id')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Pricing & Stock Settings</h6>
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Cost Price (₹)</label>
                                                        <input
                                                            type="number"
                                                            name="cost_price"
                                                            className="form-control"
                                                            value={formData.cost_price}
                                                            onChange={handleInputChange}

                                                        />
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label">Selling Price (₹)</label>
                                                        <input
                                                            type="number"
                                                            name="selling_price"
                                                            className="form-control"
                                                            value={formData.selling_price}
                                                            onChange={handleInputChange}

                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Min Stock Level</label>
                                                        <input
                                                            type="number"
                                                            name="min_stock_level"
                                                            className="form-control"
                                                            value={formData.min_stock_level}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Max Stock Level</label>
                                                        <input
                                                            type="number"
                                                            name="max_stock_level"
                                                            className="form-control"
                                                            value={formData.max_stock_level}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-4 mb-3">
                                                        <label className="form-label">Reorder Level</label>
                                                        <input
                                                            type="number"
                                                            name="reorder_level"
                                                            className="form-control"
                                                            value={formData.reorder_level}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Additional Information</h6>
                                                <div className="row">
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Description</label>
                                                        <textarea
                                                            name="description"
                                                            className="form-control"
                                                            value={formData.description}
                                                            onChange={handleInputChange}
                                                            rows="2"
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mb-3">
                                                        <label className="form-label">Status</label>
                                                        <Select
                                                            options={statusOptions}
                                                            value={statusOptions.find(opt => opt.value === formData.status)}
                                                            onChange={(selected) => handleSelectChange(selected, 'status')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleEditSubmit}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle size={16} className="me-2" />
                                            Update Raw Material
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* View Raw Material Modal - Keep same as before */}
            {showViewModal && selectedMaterial && (
                // ... (your existing view modal)
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Eye size={18} className="me-2" />
                                    Raw Material Details - {selectedMaterial.material_name}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Basic Information</h6>
                                                <p><strong>Material Code:</strong> {selectedMaterial.material_code}</p>
                                                <p><strong>Material Name:</strong> {selectedMaterial.material_name}</p>
                                                <p><strong>SKU:</strong> <code>{selectedMaterial.sku}</code></p>
                                                <p><strong>Category:</strong> {selectedMaterial.category_name}</p>
                                                <p><strong>Unit:</strong> {selectedMaterial.unit_name || selectedMaterial.unit}</p>
                                                <p><strong>Status:</strong> {getStatusBadge(selectedMaterial.status)}</p>
                                                {selectedMaterial.description && (
                                                    <p><strong>Description:</strong> {selectedMaterial.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body">
                                                <h6 className="mb-3">Pricing & Stock</h6>
                                                <p><strong>Cost Price:</strong> {formatCurrency(selectedMaterial.cost_price)}</p>
                                                <p><strong>Selling Price:</strong> {formatCurrency(selectedMaterial.selling_price)}</p>
                                                <p><strong>Current Stock:</strong>
                                                    <span className={selectedMaterial.quantity <= selectedMaterial.reorder_level ? 'text-danger fw-bold ms-1' : 'ms-1'}>
                                                        {selectedMaterial.quantity || 0} {selectedMaterial.unit_name || 'PCS'}
                                                    </span>
                                                </p>
                                                <p><strong>Min Stock Level:</strong> {selectedMaterial.min_stock_level}</p>
                                                <p><strong>Max Stock Level:</strong> {selectedMaterial.max_stock_level}</p>
                                                <p><strong>Reorder Level:</strong> {selectedMaterial.reorder_level}</p>
                                            </div>
                                        </div>
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Additional Information</h6>
                                                {selectedMaterial.supplier_name && (
                                                    <p><strong>Preferred Supplier:</strong> {selectedMaterial.supplier_name}</p>
                                                )}
                                                {selectedMaterial.hsn_code && (
                                                    <p><strong>HSN Code:</strong> {selectedMaterial.hsn_code}</p>
                                                )}
                                                <p><strong>Created:</strong> {formatDate(selectedMaterial.created_at)}</p>
                                                <p><strong>Last Updated:</strong> {formatDate(selectedMaterial.updated_at)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                {canEdit && selectedMaterial.status !== 'discontinued' && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            setShowViewModal(false);
                                            handleEdit(selectedMaterial);
                                        }}
                                    >
                                        <Edit size={16} className="me-2" />
                                        Edit Material
                                    </button>
                                )}
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RawMaterialMaster;